import { ITicketNum } from '@redux/common';
import { ITHSRStore } from '@redux/THSR/interfaces';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import minMax from 'dayjs/plugin/minMax';
import {
    IETItem,
    IFormat,
    IPlanList_Format,
    IPlanDetail,
    IGDSPlanListData,
    IGDSSessionItem,
    ITHSROpenedPanel,
    TSpec,
    TTripType,
} from '@redux/common';
import { isMobile, storageKey } from '@libs/globalVariables';

declare var Templates;

// 儲存在sessionStorage中的待送出資料格式
interface IStorage {
    productID: string; // 產品ID
    round: {
        // 高鐵單程回來會班次資訊
        departDate: string; // 出發日
        departStation: string; // 出發站
        departStationTxt: string; // 出發站中文字
        departTime: string; // 出發時間
        arriveStation: string; // 抵達站
        arriveStationTxt: string; // 抵達站中文字
        arriveTime: string; // 抵達時間
        trainNumber: string; // 班次號碼
        duration: string; // 車程時間
    }[];
    trainClass: string; // 高鐵車廂種類
    ticketsQty: {
        [key: string]: number; // 票種與數量
    };
    totalHeadCount: number; // 高鐵總人數
    THSRtotalPrice: number; // 高鐵總價
    THSRDiscountPrice: number; // 高鐵折扣金額
    THSRUnitPrice: {
        adultPrice: number; // 成人單價(如果是來回就來回加總)
        concessionPrice: number; // 優惠票單價(如果是來回就來回加總)
    };
    PassengerUseDate: string; // 選完高鐵條件下一步後所選之旅客使用日期
}

// 送出url整理所需參數
interface IFormatPassengerURL {
    ETID: string;
    ticketNum: {};
    pickCalendar?: string;
    hasCalendar?: boolean;
    pickSession?: string;
    Foreign?: number | string;
    UUID?: string;
    PassengerUseDate?: string;
    ProductDescKey?: string;
}

interface ICheckLigBoxAndDialogDisplay {
    (
        state: unknown,
        stateAction: React.Dispatch<React.SetStateAction<{ [key: string]: unknown }>>,
        name: string
    ): boolean
}

/**
 * @summary 從全域(Window)中提取IT提供的變數和函式
 * @returns 一個物件包含所有提供的變數和方法
 */
export const getWindowCustomFunctions = () => {
    /*
        2022/09為了加速頁面loading速度，取消一開始就打 ThsrDefaultItem API(HasTHSR)來取得高鐵面板參數，
        IT會在頁面loading時，宣告一個 isThsr 變數並放入"判斷是否有高鐵"的布林值
        TODO: 本機端開發環境請直接修改 defaultData 後面的參數(boolean參數預設false)來模擬產品在測試機的設定
    */
    const devETID = '145008'; // 開發用 ETID

    const defaultData = {
        ETID: process.env.SERVER !== 'dev' ? null : devETID, // 產品ID
        isThsr: false, // 是否是可搭配高鐵的產品
        isLogin: false, // 會員登入判斷(預設false)
        isDisney: false, // 是否為迪士尼產品 (跳頁前要顯示光箱)
        isErpOrdr: false, // 是否是電話單下單的產品
        formatPriceByCulture: formatPriceByCulture // 總金額依照語系格式化的方法
    }

    let windowObj = Object.keys(defaultData).reduce((acc,cur) => {
        let data = window[cur];
        if(typeof data === 'undefined') {
            if (process.env.SERVER !== 'dev') console.log(`${cur} data not found ! Use defaultData...`);
            data = defaultData[cur];
        };
        acc[cur] = data;
        return acc;
    }, {});

    return windowObj;
}


/**
 * @summary 判斷是否為GDS產品
 * @param prodID 產品編號(ETID)
 * @returns 是否為GDS之 boolean
 */
export const isGDS = (prodID: string) => {
    const gRegExp = /^G/;
    return gRegExp.test(prodID);
};

/**
 * @summary 從cookie提取指定欄位的值
 * @param name 要找尋的cookie key
 * @description 目前設定的語言會存在cookie的_culture
 * @returns 指定的cookie value || ''
 */
export const getCookieValueByRegEx = (name: string) => {
    return document.cookie.match('(^|;)\\s*' + name + '\\s*=\\s*([^;]+)')?.pop() || ''
};

/**
 * @summary 切換目前頁面指定的語系
 * @param i18n i18n核心邏輯
 * @param lang 傳入的語系代碼(沒有傳入會預設中文)
 * @description 根據傳入的語系代碼來切換成該語系版本
 * @returns no return
 */
export const changeLang = (i18n: any, lang?: string) => {
    let currentLang = lang ? lang : 'zh-tw'; // 預設中文
    const localesFileStr = currentLang.replace('-','').toUpperCase()

    i18n.changeLanguage(localesFileStr)
};

/**
 * @summary 將價格數字以逗號將每千分分隔
 * @param price 想要轉換之價錢 number ex: 1234567
 * @returns 轉換後之價錢string ex: "1,234,567"
 */
export const addSeparator = (price: number) => {
    return price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

/**
 * @summary 將千分逗號格式價錢字串轉乘number
 * @param price 想要轉換之價錢 ex: "1,234,567"
 * @returns 轉換後之價錢 number ex: 1234567
 */
export const priceStrToNum = (price: string) => {
    return Number(price?.split(',').join(''));
};

/**
 * @summary 將價格數字依照語系進行格式化 [此方法僅本地使用，測試機和正式機是從全域中提取IT的共用函式]
 * @param currency 該語系使用的幣別 string ex: TWD
 * @param price 想要轉換之價錢 number ex: 1234567
 * @returns 依照該國格式轉換後的價錢 number
 */
export const formatPriceByCulture = (currency: string, price: number, fractionDigits?: number) => {
    const cultures = {
        'TWD': 'zh-TW',
        'USD': 'en-US',
        'JPY': 'ja-JP'
    };

    let options: {
        style?: string,
        currency: string,
        useGrouping: boolean,
        maximumFractionDigits?: number
    } = {
        currency: currency,
        useGrouping: false
    };

    if (typeof fractionDigits == 'number') {
        options.maximumFractionDigits = fractionDigits;
    }

    let numberFormat = new Intl.NumberFormat(cultures[currency], options);
    let formattedPrice = numberFormat.format(price);
    return +formattedPrice;
}

/**
 * @summary 計算傳送參數productDetailOrder的值
 * @param ticketNum 用於代入票種價錢區塊之物件
 * @description 使用在 formatPassengerURL 和 apiCall裡面的fetchProductOrderValidity(驗價API)
 * @returns 要送到旅客頁的productDetailOrder值 ex:'2_1'
 */
export const formatProductDetailOrder = (ticketNum: ITicketNum) => {
    let order = Object.keys(ticketNum)
        .reduce((accumulator, currentValue) => {
            if (ticketNum[currentValue] > 0) {
                accumulator += `${currentValue}_${ticketNum[currentValue]},`;
            }
            return accumulator;
        }, '')
        .slice(0, -1);
    return order;
}

/**
 * @summary 將傳入參數組成所需url資訊
 * @param ETID 產品編號(ETID)
 * @param ticketNum 用於代入票種價錢區塊之物件
 * @param pickCalendar 已選日期
 * @param hasCalendar 是否是有月曆的產品(暫時只有自建產品要傳)
 * @param pickSession 已選場次時間
 * @param Foreign 是國內還國外(url上Foreign參數，暫時只有GDS產品要傳)
 * @param UUID 已選場次之UUID(暫時只有GDS產品要傳)
 * @returns 組合後用之送出url參數
 */
export const formatPassengerURL = ({
    ETID,
    ticketNum,
    pickCalendar,
    hasCalendar = false,
    pickSession,
    Foreign,
    UUID,
    ProductDescKey
}: IFormatPassengerURL) => {
    let order = formatProductDetailOrder(ticketNum);
    let date = pickCalendar?.replace(/-/g, '/');

    if (isGDS(ETID)) {
        if (pickSession === '') { // GDS沒有場次時不用傳送 productUseTimeUUID 參數
            return `?productCode=${ETID}&productDetailOrder=${order}&productUseDate=${date}&productUseTime=${pickSession}&Foreign=${Foreign}${ProductDescKey ? `&Key=${ProductDescKey}` : ''}`;
        } else {
            return `?productCode=${ETID}&productDetailOrder=${order}&productUseDate=${date}&productUseTime=${pickSession}&Foreign=${Foreign}&productUseTimeUUID=${UUID}${ProductDescKey ? `&Key=${ProductDescKey}` : ''}`;
        }
    } else {
        if (hasCalendar) {
            return `?productCode=${ETID}&productDetailOrder=${order}&productUseDate=${date}&productUseTime=${pickSession}${ProductDescKey ? `&Key=${ProductDescKey}` : ''}`;
        } else {
            return `?productCode=${ETID}&productDetailOrder=${order}&productUseDate=${date}${ProductDescKey ? `&Key=${ProductDescKey}` : ''}`;
        }
    }
};

/**
 * @summary 呼叫 header #lionLogin 的會員登入光箱，完成登入後進行轉址
 * @param url 要跳轉的網址
 * @returns no return
 */
export const openMemberLightBox = (url: string, closeFunction?: () => void) => {
    // 關閉光箱觸發的事件(要寫在設定URL之前，避免無法跳轉)
    Templates.login_lion.default.ResetLoginInit(
        {closeLightBoxFn: closeFunction},
    )
    const lightSpeedProduct = encodeURIComponent(JSON.stringify({
        'productID': '7', // 票卷代碼
        'redirectUrl': encodeURIComponent(url),
    }));
    const pcLoginHandler = () => {
        // 應急方法，等站長調整呼叫邏輯後，需更改為站長方法，且移除jq cdn
        $("#lionLogin").trigger("setUrlParams", [
        { lightSpeedProduct },
        "login",
        ]);
        $('#lionLogin').trigger('open', ['login']);
    };

    const mobileLoginHandler = () => {
        const memberDomain =
            process.env.SERVER === 'prod' ? '//member.liontravel.com' : '//umember.liontravel.com';

            const mobileLoginUrl = `${memberDomain}/Account/Login?lightSpeedProduct=${lightSpeedProduct}`;

            window.location.href = mobileLoginUrl;

        window.location.href = mobileLoginUrl;
    };
    const handleLogin = isMobile ? mobileLoginHandler : pcLoginHandler;
    handleLogin();
};

/**
 * @summary 判斷API回傳字串是否符合JSON格式
 * @param str 需判斷之json string
 * @returns 是否為json string 之 boolean
 */
export const isJsonString = (str: string) => {
    try {
        JSON.parse(str);
    } catch (e) {
        return false;
    }
    return true;
};

/** 
 * @summary 將entity tag string covert 成 HTML tag string
 *  EX: &lt;br /&gt;   => <br/>
 */
export const decodeHTMLstring = (str: string) => {
    if (str === '' || !str) return '';
    let re = /script/gi;
    const a = str.replace(re, '');
    const ele = document.createElement('span');
    ele.innerHTML = a;
    return ele.textContent || ele.innerText;
};

/** 
 * @summary 回傳計算完的票種初始張數 
 * @param ETItem 想要計算之品項
 * @returns 根據票種倍數規則計算完後之張數 number
 */
export const calTicketLowQty = (ETItem: IETItem) => {
    if (ETItem.LowQty === 0) return 0;
    return (
        ETItem.LowQty > ETItem.Mutiple 
            ? ETItem.Mutiple === 1 //大於倍數時，要用倍數的值去做倍數運算
                ? Math.floor(ETItem.LowQty / ETItem.Mutiple)
                : ETItem.Mutiple * (Math.floor(ETItem.LowQty / ETItem.Mutiple) + 1)
            : ETItem.Mutiple //當最小數量小於倍數時就直接用倍數的值(倍數*1的意思) 
    ) 
};

/** @summary 回傳票種已選物件  */
export const convertTicketNum = (ETItems: IETItem[]) => {
    let initAllTicketNum = {};
    if (!ETItems) return null;
    ETItems.forEach((item) => initAllTicketNum[item.ItemSeq] = calTicketLowQty(item));

    return initAllTicketNum;
};

/** 
 * @summary M版方案側開後bodyfix設定
 * @param setFixed 設定是否要fixed body and html
 * @returns no return
 */
export const handletBodyFixed = (setFixed: boolean) => {
    if (isMobile) {
        const getHtml = document.querySelector('html');
        const getele = document.querySelector('body');
        const elementStyle = getele.style;
        const htmlStyle = getHtml.style;
        const btop = getele.getBoundingClientRect().top;
        if (setFixed) {
            elementStyle.overflow = 'hidden';
            elementStyle.position = 'fixed';
            elementStyle.width = '100%';
            elementStyle.top = `${btop}px`;
            elementStyle.right = '0';
            elementStyle.bottom = '0';
            elementStyle.left = '0';
            htmlStyle.overflow = 'hidden';
        } else {
            const removeStyle = ['overflow', 'width', 'position', 'top', 'right', 'bottom', 'left'];
            removeStyle.map((item) => elementStyle.removeProperty(item));
            htmlStyle.removeProperty('overflow');
            window.scrollTo({
                top: -btop,
            });
        }
    }
};

/** @summary 找到符合原可選日期的第一個，因為原可選日期可能不是區間，會有某幾天disabled */
export const minAbleDate = ({ ableDates, mindate }) => {
    dayjs.extend(isSameOrAfter);
    return ableDates.find((abledate: string) => dayjs(abledate).isSameOrAfter(mindate));
};

/** @summary 重置資料 */
export const resetAll = ({
    ExpectedUseDay,
    EarliestBuyDay,
    context,
    dispatch,
}: {
    ExpectedUseDay: boolean;
    EarliestBuyDay: string;
    context: any;
    dispatch?: any;
}) => {
    const {
        GTempCalendarData,
        GDetail,
        setTicketNum,
        setCalendar,
        session,
        setSessionPicked,
        setTGCalenderErr,
        setTGSessionErr,
        setTGTicketErr,
    } = context;
    const EIItem = ExpectedUseDay ? GTempCalendarData[GDetail?.EarliestBuyDay]?.ETItem : GDetail?.ETItem;
    dispatch(setTicketNum(convertTicketNum(EIItem)));
    dispatch(setCalendar(dayjs(GDetail?.EarliestBuyDay).format('YYYY-MM-DD')));
    dispatch(setTGCalenderErr(''));
    dispatch(setTGSessionErr(''));
    dispatch(setTGTicketErr(''));
    GDetail.Sessions && session && dispatch(setSessionPicked(session[0]));
    // if (isMobile) {
    // 有月曆的產品滑到月曆區塊否則滑到票種區塊
    // if (ExpectedUseDay) {
    //     calendarBlockRef.current.scrollIntoView({ behavior: 'smooth' });
    // } else {
    //     ticketBlockRef.current.scrollIntoView({ behavior: 'smooth' });
    // }
    // }
};

/** 
 * @summary 取得GDS當前選擇之方案
 * @param formatId 已選方案之 FormatID 參數
 * @param GListData 該GDS所有方案資料 (Format 參數)
 * @returns Format 陣列中符合已選方案之資料物件
 */
export const getNowFormat = ({ formatId, GListData }: { formatId: string,
    GListData: IGDSPlanListData }) => {
    // const lists = PlanContext?.GListData;
    return GListData?.Format.filter((item) => item.FormatID === formatId)[0];
};


/** 
 * @summary 自建初始化票券張數 TODO:目前已改使用 convertTicketNum，檢查無使用後可刪除
 * @param DetailData 自建方案打API後response之資料
 * @returns 用於代入票種價錢區塊之物件
 */
export const initTicketNum = (Item: any) => {
    let initAllTicketNum = {};
    Item.forEach((item) => {
        initAllTicketNum[item.ItemSeq] = 0;
    });
    return initAllTicketNum;
};

/** 
 * @summary 觸發未填之錯誤樣式class
 * @param ref 需要觸發的區塊
 * @param timer settimeout timer
 * @returns no return
 */
export const triggerErrShake = ({ ref, timer }) => {
    ref.current?.classList.add('errorShake');
    timer = setTimeout(() => {
        ref.current?.classList.remove('errorShake');
    }, 1000);
};

/** 
 * @summary 觸發滑動到某元素
 * @param ref 需要滑動到的
 * @returns no return
 */
export const scrollToElement = ({ ref }) => {
    // const top = ref.current.getBoundingClientRect().top;
    ref.current?.scrollIntoView({ behavior: 'smooth' });
};

/** 
 * @summary GDS有場次的取得目前選到場次之ETITEM
 * @returns 符合已選的session物件
 */
export const getSessionInfo = (context: {
    session: IGDSSessionItem[];
    sessionPicked: IGDSSessionItem;
}) => {
    const { session, sessionPicked } = context;
    if (!session || !sessionPicked) return;
    return session.find((item) => item.SessionUUID === sessionPicked?.SessionUUID);
};

/** 
 * @summary GDS有場次的取得擁有最低價錢的場次
 * @returns 符合最低價的session物件
 */
export const getLowestPriceSession = (session: IGDSSessionItem[]) => {
    if (!session) return;
    const sessionCopy = JSON.parse(JSON.stringify(session));
    const sorted = sessionCopy.sort((a: any, b: any) => a.LowestPrice - b.LowestPrice);
    return sorted[0];
};

/** 
 * @summary 取得多個日期陣列共有日期
 * @param allArrays 日期陣列1 ['2021-10-21', '2021-10-09', '2021-10-13']
                    日期陣列2 ['2021-10-09']
                    日期陣列3 ['2021-10-07', '2021-10-09']
 * @returns 共同有的日期  ex: ['2021-10-09']
 */
export const getCommonAbleDates = (allDatesArrays: string[][]) => {
    if (!allDatesArrays || allDatesArrays.length === 0) return [];
    const allArraysCopy = JSON.parse(JSON.stringify(allDatesArrays));
    const commonAbleDates = allArraysCopy.shift()?.filter(function (v: string) {
        return allArraysCopy.every(function (a: string[]) {
            return a?.indexOf(v) !== -1;
        });
    });
    return commonAbleDates;
};

/** 
 * @summary 取得detail物件中CalendarDate或NotIncludeDate的所有日期
 * @param detailData 自建(DetailData) / GDS(GDetail)
 * @param name 要取出的欄位名稱(calendarDate || notIncludeDate => 決定之後要進行的處理)
 * @returns 空陣列 | 陣列包含所有的日期字串  ex: ['2021-10-09','2021-10-10']
 */
export const getAllDatesInDetailData = (detailData: IPlanDetail, name: string) => {
    if(!detailData) return [];
    let allDatesArr;
    switch (name) {
        case 'calendarDate':
            allDatesArr = Object.keys(detailData.CalendarDate)
            return allDatesArr;
        case 'notIncludeDate':
            allDatesArr = []
            detailData.NotIncludeDate.forEach((obj) => {
                allDatesArr.push(obj.StartDate)
            })
            return allDatesArr;
        default:
           return [];
    }
};

/** 
 * @summary 取得日期陣列最早的那天
 * @param dates 日期陣列 ['2021-10-21', '2021-10-09', '2021-10-13']
 * @returns 日期陣列中最早的那天  ex: '2021-10-09'
 */
export const getEarliestDate = (dates: string[]) => {
    if (!dates || dates.length === 0) return null;
    dayjs.extend(minMax); //This dependent on MinMax plugin to work
    const formatKeys = dates.map((item: string) => {
        if (dayjs(item, 'YYYY-MM-DD').isValid()) return dayjs(item);
    });
    const firstDate = dayjs.min(formatKeys).format('YYYY-MM-DD');
    return firstDate;
};

/** 
 * @summary 取得日期陣列最晚的那天
 * @param dates 日期陣列 ['2021-10-21', '2021-10-09', '2021-10-13']
 * @returns 日期陣列中最早的那天  ex: '2021-10-21'
 */
export const getLatestDate = (dates: string[]) => {
    if (!dates || dates.length === 0) return null;
    const formatKeys = dates.map((item: string) => {
        if (dayjs(item, 'YYYY-MM-DD').isValid()) return dayjs(item);
    });
    const lastDate = dayjs.max(formatKeys).format('YYYY-MM-DD');
    return lastDate;
};

/** 
 * @summary 高鐵暫存資料清空
 * @returns no returns
 */
export const THSRClearStorage = () => {
    // 從sessionStorage移除之前存的資料
    sessionStorage.removeItem(storageKey);
};

/** 
 * @summary 將需要的資料到sessionStorage 暫存/提取/刪除
 * @param action 要對sessionStorage做的動作 
 *               'save': 存取 / 'get': 取得 / 'remove': 刪除
 * @param key sessionStorage裡命名的key值
 * @param submitParams 要送出的所需參數(save用)
 * @returns 'save' => no returns
 *          'get'  => 取得儲存的指定參數
 *          'remove' => 刪除儲存的指定參數
 */
export const manageSessionStorage = (action: string, key: string, submitParams?: string | Object) => {
    switch(action) {
        case 'save':
            sessionStorage.setItem(key, JSON.stringify(submitParams))
        return;
        case 'get':
            return sessionStorage.getItem(key)
        case 'remove':
            sessionStorage.removeItem(key)
        return;
    }
};

/** 
 * @summary 篩選出高鐵單程或來回的物件資訊
 * @param thsrTripInfos 高鐵initData裡的ThsrTripInfos陣列
 * @param THSRType 目前所需要的面板是單程還來回
 * @returns 單程或來回的個別物件 <IThsrTripInfo>
 */
export const getTHSRTripInfo = (thsrTripInfos, THSRType) => {
    if (!thsrTripInfos || !THSRType) return null;
    if (THSRType === TTripType.round) { //來回面板
        return thsrTripInfos.find((child) => child.ThsrType === TTripType.round); // "1"
    } else { //單程面板
        return thsrTripInfos.find((child) => child.ThsrType === TTripType.oneway); // "0"
    }
};

/** 
 * @summary 篩選過高鐵規則後用於render高鐵裡的的方案或品項items
 * @param listItems list的Format陣列
 * @param formatIDArr 從getTHSRTripInfo篩選出來的單程或來回物件的品項/方案ID
 * @returns Format陣列中篩選出來符合ID的方案或品項物件，並用新陣列包住 ex:[{}] ; 若沒有符合就是空陣列
 * TODO: 目前未使用
 */
export const THSRMapItems = ({
    listItems,
    formatIDArr
}: {
    listItems: [];
    formatIDArr: [ { FormatID: string; EarliestBuyDay: string; } ]
}) => {
    if(!listItems || !formatIDArr) return [];
    const FinalETItems = listItems?.filter((item: IFormat | IPlanList_Format) => {
        return formatIDArr.find((i) => i.FormatID === item.FormatID) ? true : false
    });
    return FinalETItems || [];
};

/** 
 * @summary 篩選過高鐵規則後用於render原始方案或品項items
 * @param listItems 自建就是ETITEMS陣列、GDS就是FORMAT陣列
 * @returns 根據是GDS還是自建return要render的方案列表或是品項列表
 * FIXME: 目前只有GDS使用(舊架構)，之後可直接刪除
 */
export const MapItems = ({ listItems }: { listItems: any }) => {
    const FinalETitems = listItems?.filter((item: IFormat | IPlanList_Format) => {
        switch (item.THSRspec) {
            case TSpec.must:
                return false;
            case null:
            case 'NULL':
            case TSpec.optional:
            default:
                return true;
        }
    });
    return FinalETitems || [];
};

/** 
 * @summary 格式化出用於高鐵驗證庫存(apiCall.ts裡的getCheckThsrQty方法)的reqBody
 * @param Rounds 一個陣列包含各車次的詳細資料(起訖車站&代號、發車時間、車廂種類...etc)
 * @param THSRDepartTrain 出發的車次資料
 * @param THSRReturnTrain 回程的車次資料
 * @param THSRInfo 車次票價相關資料
 * @returns getCheckThsrQty 的 reqBody
 */
export const formatThsrQtyReqBody = (
    detailRounds: ITHSRStore['THSRDetailData']['Rounds'],
    ETID: string,
    THSRDepartTrain: ITHSRStore['THSRDepartTrain'] | undefined,
    THSRReturnTrain: ITHSRStore['THSRReturnTrain'] | undefined,
    THSRInfo: ITHSRStore['THSRInfo'] | undefined,
) => {
    const defaultData = {
        Etcode: ETID,
        DepartStation: '',
        ArriveStation: '',
        DepartDate: '',
        Passenger: '',
        DepartTime: '',
        TrainNumber: '',
        TrainClass: '',
    };

    const getDefault = (key: string, child: ITHSRStore['THSRDetailData']['Rounds'][number]) => {
        const value = child[key]?.value; // DepartStation & ArriveStation
        return value !== undefined ? value : defaultData[key];
    };

    const reqBody = detailRounds.map((child, index) => {
        const roundObj = Object.keys(defaultData).reduce((acc,cur) => {
            let data;
            if (cur !== 'Etcode') {
                if (['DepartStation', 'ArriveStation', 'DepartDate'].includes(cur)) {
                    data = cur === 'DepartDate' ? child[cur] : getDefault(cur, child);
                } else if (cur === 'Passenger') {
                    data = THSRInfo?.TicketsInfo?.headCounts || getDefault(cur, child);
                } else {
                    const trainData = index === 0 ? THSRDepartTrain : THSRReturnTrain;
                    data = trainData ? trainData[cur]?.replace(':', '') : getDefault(cur, child);
                }
                acc[cur] = data;
            }

            acc['Etcode'] = defaultData['Etcode'];
            return acc;
        }, {});
    
        return roundObj;
    });

    return reqBody;
}

/** 
 * @summary 取得自建產品高鐵條件下所剩ETITEMS中最低價格
 * @param items 要計算的ETITEMS
 * @returns 最低價格 number
 * TODO: 目前未使用
 */
export const getLowestETItemsPrice = (items: IETItem[]) => {
    const prices = items.map((item) => item.SalePrice_NonMember);
    if (!prices) return null;
    return Math.min(...prices);
};

/** 
 * @summary 由起末日期算出日期陣列
 * @param start 開始日期
 * @param end 結束日期
 * @returns 日期陣列 string[]
 */
export const returnDatesArr = (start: string, end: string) => {
    dayjs.extend(isSameOrAfter);
    let arr = [];
    let currentDate = start;
    while (dayjs(end).isSameOrAfter(currentDate)) {
        arr.push(dayjs(currentDate).format('YYYY-MM-DD'));
        currentDate = dayjs(currentDate).add(1, 'day').format('YYYY-MM-DD');
    }
    return arr;
};

/** 
 * @summary 由起末日期算出日期陣列
 * @param _min 開始日期
 * @param _max 結束日期
 * @returns 日期陣列 string[]
 */
export const generateDates = (_min: string, _max: string) => {
    if (!_min || !_max) return [];
    const daysLength = dayjs(_max).diff(_min, 'day') + 1;
    const rangeDates = [];
    for (let i = 0; i < daysLength; i++) {
        rangeDates.push(dayjs(_min).add(i, 'day').format('YYYY-MM-DD'));
    }
    return rangeDates;
};

/** 
 * @summary 融合新舊日期陣列資料，篩選掉重複的日期 
 * @param arr1 要融合的原陣列
 * @param arr2 要融合的新陣列
 * @description 目前用來融合兩個月的CalendarDate資料，並確保相同日期的物件只有一個
 * @returns 包含日期物件的陣列
 */
export const getNotIncludeDateSet = (arr1, arr2) => {
    const data = arr1.concat(arr2);
    const dataCounter = {};
    const filteredData = [];

    data.forEach((item) => {
        const key = item.StartDate + item.EndDate;

        if (!dataCounter[key]) {
            dataCounter[key] = 1;
            filteredData.push(item);
        }
    });
    return filteredData;
};

/** 
 * @summary 將object轉成string(保留undefined的值)
 * @param object 包含要檢查項目的物件
 * @description 用來檢查checkLigBoxOrDialogDisplay內是否有少傳參數，
 *              若值為undefined會轉換成null輸出，方便開發畫面報錯檢查
 * @returns boolean
 */
const objectToString = (object = {}) => {
    return JSON.stringify(
        object,
        (key, value) => value === undefined ? null : value
    );
}

/** 
 * @summary LightBox以及Dialog是否顯示判斷
 * @param state useState variable
 * @param stateAction useState action
 * @param name LightBox or Dialog的名字(請輸入在object內的key值)
 * @description 請使用useState定義一個object後，再傳入此function做判斷
 * @returns boolean
 */
export const checkLigBoxOrDialogDisplay:ICheckLigBoxAndDialogDisplay = (state,stateAction,name) => {
    const messageHead = 'checkLigBoxAndDialogDisplay'; //錯誤訊息開頭
    
    if(
        state === undefined || 
        stateAction === undefined ||
        name === undefined
    ) throw new Error(`${messageHead} 缺少傳入參數：${objectToString({state, stateAction, name})}`);

    if(
        !!state &&
        !!stateAction &&
        state[name] !== undefined && 
        !state[name]
    ) return true
    else return false
}

/** 
 * @summary 在不同錯誤下要顯示的錯誤提示 (TODO:目前多語系JSON有規劃類似功能，考慮融合多語系一起使用)
 * @param state 打API時發生的錯誤情況
 * @description 'noListFormat': list API沒有Format
 *              'noCalendarDate': 打detail API時沒有回傳calendarDate
 *              'default': 其他bug
 *              後續有新提示要新增也可以統一在此管理
 * @returns 錯誤提示文字
 */
export const getErrMsg = (state: string | null = null) => {
    switch (state) {
        case 'noListFormat':
            return `資料獲取失敗，請重整頁面`
        case 'noCalendarDate':
            return `暫無可銷售的日期，請選擇其他方案`
        default:
           return `取得方案細節失敗`;
    }
}

/** 
 * @summary 將api回傳的 promise JSON化成一般物件或值
 * @param api 一個方法內含要打的 api function
 * @description 
 * @returns 原本api裡的值
 */
export const parseApiResponse = async(api:() => Promise<Response>) => {
    try {
        const apiResponse = await api();
        const data = await apiResponse.json();
        console.log("api回傳的data", data)
        
        return data;  
    } catch (error) {
        console.error(`Error parsing API response: ${error.message}`);
        throw error;
    }  
};
